import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { getApiUrl } from "@/lib/config";
import { useLanguage } from "@/components/language-provider";
import { formatPhoneDisplay } from "@/lib/masks";
import { Plus, Star, Users } from "lucide-react";

export interface LookupContact {
  id: string;
  name: string;
  /** Default e-mail (personal or work, as flagged on the contact). */
  email: string;
  personalEmail?: string | null;
  corporateEmail?: string | null;
  preferredEmail?: "personal" | "corporate" | null;
  phone?: string | null;
  companyName?: string | null;
  cpf?: string | null;
  cnpj?: string | null;
  jobTitle?: string | null;
  photoUpdatedAt?: string | null;
}

export interface LookupEmail { kind: "personal" | "corporate"; email: string; isDefault: boolean }

/** The contact's e-mails, default first. */
export function contactEmails(c: Pick<LookupContact, "email" | "personalEmail" | "corporateEmail" | "preferredEmail">): LookupEmail[] {
  const list: LookupEmail[] = [];
  const preferred = c.preferredEmail === "corporate" ? "corporate" : "personal";
  if (c.personalEmail) list.push({ kind: "personal", email: c.personalEmail, isDefault: preferred === "personal" || !c.corporateEmail });
  if (c.corporateEmail) list.push({ kind: "corporate", email: c.corporateEmail, isDefault: preferred === "corporate" || !c.personalEmail });
  if (list.length === 0 && c.email) list.push({ kind: "personal", email: c.email, isDefault: true });
  return list.sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
}

export const mapLookupContact = (c: any): LookupContact => ({
  id: String(c.id), name: c.name || "", email: c.email || "", personalEmail: c.personalEmail ?? null,
  corporateEmail: c.corporateEmail ?? null, preferredEmail: c.preferredEmail ?? null, phone: c.phone ?? null,
  companyName: c.companyName ?? null, cpf: c.cpf ?? null, cnpj: c.cnpj ?? null, jobTitle: c.jobTitle ?? null,
  photoUpdatedAt: c.photoUpdatedAt ?? null,
});

// Once the API says the CRM module is off (or the endpoint is missing), stop asking for the session.
let lookupUnavailable = false;

type Props = Omit<React.ComponentProps<typeof Input>, "value" | "onChange"> & {
  value: string;
  onChange: (value: string) => void;
  /** Picked contact and, in "signer" mode, the e-mail picked (one of its two). */
  onPick: (contact: LookupContact, email: string) => void;
  /**
   * "signer": each e-mail of a contact is an option (the default one flagged) — send wizard.
   * "client": one option per contact — the client field of a lead.
   */
  mode?: "signer" | "client";
  /** Characters typed before searching; 0 lists the most recent contacts on focus. */
  minChars?: number;
  /** Extra action at the end of the list ("Register a new contact"), with what was typed. */
  onCreate?: (typed: string) => void;
  createLabel?: string;
};

type Option = { contact: LookupContact; email: LookupEmail; first: boolean };

/**
 * Text input that suggests registered contacts (CRM "Clientes") while typing, like a lookup field:
 * pick one to fill the whole signer. Keyboard: ↑ ↓ to move, Enter to pick, Esc to close.
 */
export const ContactLookupInput = forwardRef<HTMLInputElement, Props>(function ContactLookupInput(
  { value, onChange, onPick, onKeyDown, onBlur, onFocus, mode = "signer", minChars = 2, onCreate, createLabel, ...inputProps },
  ref,
) {
  const { language } = useLanguage();
  const pt = language === "pt";
  const [results, setResults] = useState<LookupContact[]>([]);
  const [searched, setSearched] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const timer = useRef<number | undefined>(undefined);
  const controller = useRef<AbortController | null>(null);
  const typed = useRef(false);

  useEffect(() => () => {
    window.clearTimeout(timer.current);
    controller.current?.abort();
  }, []);

  const options: Option[] = useMemo(() => results.flatMap((c) => {
    const emails = contactEmails(c);
    if (mode === "client" || emails.length === 0) return [{ contact: c, email: emails[0] || { kind: "personal" as const, email: c.email, isDefault: true }, first: true }];
    return emails.map((e, i) => ({ contact: c, email: e, first: i === 0 }));
  }), [results, mode]);
  const total = options.length + (onCreate ? 1 : 0);

  const search = (term: string) => {
    window.clearTimeout(timer.current);
    if (lookupUnavailable || term.trim().length < minChars) {
      setResults([]);
      setSearched(false);
      setOpen(false);
      return;
    }
    timer.current = window.setTimeout(async () => {
      controller.current?.abort();
      const ac = new AbortController();
      controller.current = ac;
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(getApiUrl(`/api/contacts/lookup?q=${encodeURIComponent(term.trim())}&take=8`), {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: ac.signal,
        });
        if (!res.ok) {
          if (res.status === 404) lookupUnavailable = true;
          return;
        }
        const data = await res.json();
        if (data.enabled === false) {
          lookupUnavailable = true;
          return;
        }
        const list: LookupContact[] = (data.contacts || []).map(mapLookupContact);
        setResults(list);
        setSearched(true);
        setActive(list.length ? 0 : onCreate ? 0 : -1);
        setOpen((list.length > 0 || !!onCreate) && typed.current);
      } catch {
        /* aborted or offline: no suggestions */
      }
    }, minChars === 0 && !term.trim() ? 0 : 250);
  };

  const close = () => {
    typed.current = false;
    setOpen(false);
    setResults([]);
    setSearched(false);
  };
  const pick = (o: Option) => {
    close();
    onPick(o.contact, o.email.email);
  };
  const create = () => {
    const text = value;
    close();
    onCreate?.(text);
  };
  const choose = (index: number) => (index < options.length ? pick(options[index]) : create());

  const kindLabel = (k: "personal" | "corporate") => (k === "personal" ? (pt ? "Pessoal" : "Personal") : (pt ? "Corporativo" : "Work"));

  return (
    <div className="relative">
      <Input
        ref={ref}
        {...inputProps}
        value={value}
        autoComplete="off"
        onChange={(e) => {
          typed.current = true;
          onChange(e.target.value);
          search(e.target.value);
        }}
        onFocus={(e) => {
          if (minChars === 0) {
            typed.current = true;
            search(e.target.value);
          } else if (results.length && typed.current) setOpen(true);
          onFocus?.(e);
        }}
        onBlur={(e) => {
          window.setTimeout(() => setOpen(false), 120);
          onBlur?.(e);
        }}
        onKeyDown={(e) => {
          if (open && total > 0) {
            if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => (i + 1) % total); return; }
            if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => (i - 1 + total) % total); return; }
            if (e.key === "Enter" && active >= 0) { e.preventDefault(); choose(active); return; }
            if (e.key === "Escape") { setOpen(false); return; }
          }
          onKeyDown?.(e);
        }}
        aria-autocomplete="list"
        aria-expanded={open}
      />
      {open && total > 0 && (
        <div
          role="listbox"
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-80 overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-lg"
          data-testid="contact-lookup-list"
        >
          <div className="flex items-center gap-1.5 border-b px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            <Users className="h-3 w-3" /> {pt ? "Clientes cadastrados" : "Saved contacts"}
          </div>
          {options.length === 0 && searched && (
            <p className="px-3 py-2 text-xs text-muted-foreground" data-testid="contact-lookup-empty">{pt ? "Nenhum cliente encontrado." : "No contacts found."}</p>
          )}
          {options.map((o, i) => {
            const single = mode === "client" || contactEmails(o.contact).length < 2;
            return (
              <div key={`${o.contact.id}:${o.email.email}`} className={!single && !o.first ? "" : "border-t first:border-t-0"}>
                {!single && o.first && (
                  <div className="px-3 pt-2 text-sm font-medium">
                    <span className="block truncate">{o.contact.name}</span>
                    {o.contact.companyName && <span className="block truncate text-xs font-normal text-muted-foreground">{o.contact.companyName}</span>}
                  </div>
                )}
                <button
                  type="button"
                  role="option"
                  aria-selected={i === active}
                  onMouseDown={(e) => { e.preventDefault(); pick(o); }}
                  onMouseEnter={() => setActive(i)}
                  className={`block w-full px-3 py-2 text-left text-sm ${i === active ? "bg-accent" : ""} ${single ? "" : "pl-5"}`}
                  data-testid={`contact-lookup-item-${i}`}
                  data-email={o.email.email}
                >
                  {single && <span className="block truncate font-medium">{o.contact.name}</span>}
                  <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
                    {!single && <span className="shrink-0 rounded border px-1 py-px text-[10px] uppercase">{kindLabel(o.email.kind)}</span>}
                    <span className="truncate">{o.email.email}</span>
                    {o.email.isDefault && contactEmails(o.contact).length > 1 && (
                      <span className="inline-flex shrink-0 items-center gap-0.5 font-medium text-amber-600 dark:text-amber-400" data-testid="contact-lookup-default">
                        <Star className="h-3 w-3 fill-current" /> {pt ? "Padrão" : "Default"}
                      </span>
                    )}
                    {single && o.contact.companyName ? <span className="truncate">· {o.contact.companyName}</span> : null}
                    {single && o.contact.phone ? <span className="shrink-0">· {formatPhoneDisplay(o.contact.phone)}</span> : null}
                  </span>
                </button>
              </div>
            );
          })}
          {onCreate && (
            <button
              type="button"
              role="option"
              aria-selected={active === options.length}
              onMouseDown={(e) => { e.preventDefault(); create(); }}
              onMouseEnter={() => setActive(options.length)}
              className={`flex w-full items-center gap-2 border-t px-3 py-2 text-left text-sm font-medium text-primary ${active === options.length ? "bg-accent" : ""}`}
              data-testid="contact-lookup-create"
            >
              <Plus className="h-4 w-4" /> {createLabel || (pt ? "Cadastrar novo cliente" : "Register a new contact")}
            </button>
          )}
        </div>
      )}
    </div>
  );
});
